import React from 'react';

import {ButtonStyle} from 'ui-library/buttons/Button';
import {Header3} from 'ui-library/Headers';
import Modal from 'ui-library/modals/Modal';

import {PARAMETER_TYPE_TO_READABLE, PARAMETER_TYPE_ALLOWS_MULTIPLE_VALUES} from './ParameterConstants';
import {ParameterType} from './ParameterTypes';

export interface ParameterInfoModalProps {
  large?: boolean;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

export default function ParameterInfoModal({
  large = false,
  onRequestClose,
  onRequestSubmit,
}: ParameterInfoModalProps): JSX.Element {
  const singleValueOnlyTypes = Object.values(ParameterType)
    .filter((parameterType) => !PARAMETER_TYPE_ALLOWS_MULTIPLE_VALUES[parameterType])
    .map((parameterType) => PARAMETER_TYPE_TO_READABLE[parameterType]);

  return (
    <Modal
      title="Help with parameters"
      buttons={[{style: ButtonStyle.SECONDARY, submit: true, text: 'Done'}]}
      closeableOnEscapeKey={true}
      large={large}
      onRequestClose={onRequestClose}
      onRequestSubmit={onRequestSubmit}
    >
      <p>
        Parameters allow you to filter results on a dashboard to only those that meet certain conditions. You
        can create a parameter by replacing a value in your SQL query with the parameter name in double curly
        brackets. This allows users to later select the values they want to include in the query.
      </p>
      <Header3>Single value parameters</Header3>
      <p>
        Single value parameters will be inserted using the given value, or, in the case of object types, as
        the ID of the object in single quotes. Date inputs will be casted as dates.
      </p>
      <p>In this example, we use the given value, unless it is not defined:</p>
      <pre>
        color = {'{{parameter}}'} OR {'{{parameter}}'} IS NULL
      </pre>
      <p>
        For a value of <code>'blue'</code>, this results in:
      </p>
      <pre>color = 'blue' OR 'blue' IS NULL</pre>
      <Header3>Multi-value parameters</Header3>
      <p>
        Multi-value parameters will be inserted as an array of values. Multi-value parameters are not
        supported for{' '}
        {`${singleValueOnlyTypes.slice(0, singleValueOnlyTypes.length - 1).join(', ')}, and ${
          singleValueOnlyTypes[singleValueOnlyTypes.length - 1]
        }`}{' '}
        parameters.
      </p>
      <p>
        In this example, we check whether the value of a field is any of the given values, unless the
        parameter does not have any selected values:
      </p>
      <pre>
        field = ANY({'{{parameter}}'}) OR CARDINALITY({'{{parameter}}'}) = 0
      </pre>
      <p>
        For selected objects with IDs <code>seq_1234abcd</code> and <code>seq_2345defg</code>, this results
        in:
      </p>
      <pre>
        field = ANY(ARRAY['seq_1234abcd','seq_2345defg']::text[]) OR
        CARDINALITY(ARRAY['seq_1234abcd','seq_2345defg']::text[]) = 0
      </pre>
    </Modal>
  );
}
